import React, { Component, Fragment } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { EventWizardData, DateWarningType } from 'stores/events/creation-wizard-store';
import { DateInput } from 'widgets/form/dateInput';
import EventService, { EventDurationRequestDto, DatesCalculationRequestDto } from 'stores/events/event-service';
import { trim, replaceAll, isNullOrEmpty, isNullOrWhiteSpaces } from 'utils/useful-functions';
import { DateTimeService } from 'services/datetime-service';
import { resolve } from 'inversify.config';
import { Message as BackendMessage } from 'stores/types';
import { Form } from 'semantic-ui-react';

interface DatesStepMobileProps extends WithTranslation {
  wizardData: EventWizardData;
  dateFormat?: string;
  onValueChanged: (...changes: [string, string][]) => void;
  durationWarning: boolean;
}

interface MobileDatesStepState {
  wizardData: EventWizardData;
  warnings: DateWarningType[];
  recalculating: boolean;
}

class MobileDatesStep extends Component<DatesStepMobileProps, MobileDatesStepState> {
  @resolve(EventService)
  eventsService: EventService;

  constructor(props: DatesStepMobileProps) {
    super(props);
    const { t } = props;
    const warnings = [];
    if (props.durationWarning) warnings.push({ type: 'DURATION', text: t('There is no default calculated duration for this event') });

    this.state = { wizardData: props.wizardData, warnings, recalculating: false };
  }

  private getDurationRequestDto = (): EventDurationRequestDto => {
    const data = this.state.wizardData;
    return {
      isMachineRelated: data.isMachineRelated,
      mrRequest: data.isMachineRelated
        ? {
            originalEventTypeId: data.eventType?.originalEventTypeId,
            professionId: data.profession?.id,
            rows: (data.requestMachines || []).map(rm => ({
              clusterId: rm.cluster?.id,
              equipmentTypeId: rm.equipmentType?.id,
              machineModelId: rm.machineModel?.id,
              machineUnitsId: rm.machineUnits,
              oemId: rm.oem?.id
            })),
            trainingLevelId: data.trainingLevel?.id
          }
        : null,
      nmrRequest: !data.isMachineRelated
        ? {
            nmrClusterId: data.nmrCluster?.id ?? data.nmrClusterId,
            nmrFunctionalAreaId: data.nmrFunctionalArea?.id ?? data.nMRFunctionalAreaId,
            nmrTrainingNameId: data.nmrTrainingName?.id ?? data.nMRTrainingNameId
          }
        : null
    };
  };

  private recalculateEventDuration = () => {
    const { t } = this.props;
    const dto = this.getDurationRequestDto();

    this.setState({ recalculating: true });
    this.eventsService
      .calculateEventDuration(dto)
      .then(v => {
        let changes: [string, any][] = [['calculatedEventDuration', v]];

        const w = this.state.wizardData;
        if (isNullOrEmpty(w.plannedDuration) || w.plannedDuration === '0 w/d' || w.plannedDuration === '0') {
          changes = [...changes, ['plannedDuration', `${v}`], ['userEventDuration', v]];
        }

        this.handleValues(...changes);
        this.setState({
          recalculating: false,
          warnings: this.state.warnings.filter(x => x.type !== 'DURATION')
        });
      })
      .catch(_ => {
        this.handleValue('calculatedEventDuration', 0);
        this.setState({
          recalculating: false,
          warnings: [
            {
              type: 'DURATION',
              text: t('There is no default calculated duration for this event')
            },
            ...this.state.warnings.filter(x => x.type !== 'DURATION')
          ]
        });
      });
  };

  private getDatesCalculationRequestDto = (recalculate: 'StartDate' | 'EndDate' | 'Duration'): DatesCalculationRequestDto => {
    const data = this.state.wizardData;
    return {
      startDate: recalculate === 'StartDate' ? null : data.dateFrom,
      endDate: recalculate === 'EndDate' ? null : data.dateTo,
      durationDays: recalculate === 'Duration' ? null : data.userEventDuration,
      locationId: data.location?.id,
      pausePeriods: data.pausePeriods || []
    };
  };

  private performCalculation = (recalculate: 'StartDate' | 'EndDate' | 'Duration') => {
    const dto = this.getDatesCalculationRequestDto(recalculate);
    this.setState({ recalculating: true });
    const warnStateCalculation = this.state.warnings.filter(x => x.type !== 'CALCULATION');
    this.eventsService
      .calculateEventDates(dto)
      .then(({ item, isSuccess, messages }) => {
        const warnMsgs = (messages || []).map(x => ({ text: x.body, type: 'CALCULATION' }));
        const stateChanges: any = { recalculating: false };
        if (isSuccess && item != null) {
          const changes = {
            dateFrom: item.startDate,
            dateTo: item.endDate,
            plannedDuration: `${item.durationDays}`,
            userEventDuration: item.durationDays,
            shouldRecalculate: false
          };
          this.setState(
            ({ wizardData }) => ({ wizardData: { ...wizardData, ...changes } }),
            () => this.props.onValueChanged(...Object.keys(changes).map(x => [x as string, changes[x]] as [string, string]))
          );
        }

        stateChanges.warnings = [...warnStateCalculation, ...warnMsgs];

        this.setState({ ...stateChanges });
      })
      .catch(e => {
        const warnMsgs = (e?.response?.data?.messages || []).map((x: BackendMessage) => ({ text: x.body, type: 'CALCULATION' }));
        this.setState({ recalculating: false, warnings: [...warnStateCalculation, ...warnMsgs] });
      });
  };

  UNSAFE_componentWillReceiveProps(nextProps: DatesStepMobileProps) {
    if (
      nextProps &&
      nextProps !== this.props &&
      (nextProps.wizardData.calculatedEventDuration !== this.props.wizardData.calculatedEventDuration ||
        nextProps.durationWarning !== this.props.durationWarning)
    ) {
      let warnings = this.state.warnings;
      if (nextProps.durationWarning !== this.props.durationWarning) {
        if (!!nextProps.durationWarning) {
          warnings = warnings.filter(x => x.type !== 'DURATION');
        } else {
          const { t } = nextProps;

          warnings = [
            {
              type: 'DURATION',
              text: t('There is no default calculated duration for this event')
            },
            ...warnings.filter(x => x.type !== 'DURATION')
          ];
        }
      }

      this.setState({ wizardData: nextProps.wizardData, warnings });
    }
  }

  private handleValues = (...changes: [string, any][]) => {
    const { wizardData } = this.state;
    changes.forEach(([property, value]) => {
      wizardData[property] = value;
    });
    this.setState({ wizardData }, () => this.props.onValueChanged(...changes));
  };

  private handleValue = (property: keyof EventWizardData, value: any) => {
    const wizardData = { ...this.state.wizardData };
    const { dateFrom, dateTo } = wizardData;
    wizardData[property as string] = value;
    const changes = [];

    if (property === 'dateFrom' || property === 'dateTo') {
      const current = value && DateTimeService.toMoment(value);
      const start = dateFrom && DateTimeService.toMoment(dateFrom);
      const end = dateTo && DateTimeService.toMoment(dateTo);
      changes.push([property, value]);
      if (property === 'dateFrom') {
        if (current && end && current.isAfter(end, 'd')) {
          wizardData.dateTo = current.toISOString();
          changes.push(['dateTo', DateTimeService.toString(current)]);
        }
      } else {
        if (current && start && start.isAfter(current, 'd')) {
          wizardData.dateTo = start.toISOString();
          changes.push(['dateTo', DateTimeService.toString(start)]);
        }
      }
    } else if (property === 'plannedDuration') {
      const val = parseInt(trim(replaceAll(value || '', 'w/d', '')) || '0');
      if (Number.isNaN(val)) {
        wizardData.plannedDuration = '0';
        wizardData.userEventDuration = 0;
        changes.push([property, '0']);
        changes.push(['userEventDuration', 0]);
      } else {
        changes.push([property, value]);
        changes.push(['userEventDuration', val]);
        wizardData.userEventDuration = val;
      }
    } else changes.push([property, value]);
    this.setState({ wizardData }, () => this.props.onValueChanged(...changes));
  };

  render() {
    const { t } = this.props as any;
    const { wizardData, warnings, recalculating } = this.state;

    const hasDateFrom = !isNullOrWhiteSpaces(wizardData.dateFrom);
    const hasDateTo = !isNullOrWhiteSpaces(wizardData.dateTo);
    const hasDates = hasDateFrom && hasDateTo;
    const hasPlannedDuration = !isNullOrWhiteSpaces(wizardData.plannedDuration);
    return (
      <>
        <Form loading={recalculating} className="wizard__form__margin wizard__step-dates-wrapper">
          <div className="required field mb-3">
            <label className="block text-sm text-gray-700 mb-1">{t('Calculated Duration')}</label>
            <input
              id="events__step3__event-duration__input"
              type="number"
              className="w-full p-2 border border-gray-300 rounded"
              value={`${wizardData?.calculatedEventDuration || ' - '} w/d`}
              disabled
            />
          </div>

          <div className="required field mb-3">
            <label className="block text-sm text-gray-700 mb-1">{t('Events Dates')}</label>
            <div className="flex flex-col">
              <DateInput
                iconPosition="right"
                className="w-full p-2 border border-gray-300 rounded"
                id="date-step__date-input__left"
                required
                value={wizardData.dateFrom}
                onChange={(_, data) => this.handleValue('dateFrom', data)}
                placeholder="From"
              />

              {hasDateTo && hasPlannedDuration && (
                <span className="recalc-command" onClick={() => this.performCalculation('StartDate')}>
                  {t('Recalculate start date')}
                </span>
              )}
            </div>

            <div className="flex flex-col">
              <DateInput
                iconPosition="right"
                className="w-full p-2 border border-gray-300 rounded"
                id="date-step__date-input__right"
                required
                value={wizardData.dateTo}
                onChange={(_, data) => this.handleValue('dateTo', data)}
                minDate={wizardData.dateFrom || null}
                forceOpen={!!wizardData?.dateFrom}
                initialValue={wizardData.dateFrom}
                placeholder="To"
              />

              {hasDateFrom && hasPlannedDuration && (
                <span className="recalc-command" onClick={() => this.performCalculation('EndDate')}>
                  {t('Recalculate end date')}
                </span>
              )}
            </div>
          </div>

          <div className="required field mb-3">
            <label className="block text-sm text-gray-700 mb-1">{t('Event Duration')}</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                className="w-full p-2 border border-gray-300 rounded"
                placeholder="Event Duration"
                value={`${wizardData.plannedDuration}`}
                disabled={false}
                onChange={e => this.handleValue('plannedDuration', e.target.value)}
              />
              <span className="whitespace-nowrap">w/d</span>
            </div>
            {hasDates && (
              <span className="recalc-command" onClick={() => this.performCalculation('Duration')}>
                {t('Recalculate duration')}
              </span>
            )}
          </div>
        </Form>

        {(warnings || []).length > 0 && (
          <div className="bg-yellow-50 border border-yellow-300 rounded p-3 mt-4 flex items-start gap-2">
            <svg className="w-5 h-5 text-yellow-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11.652 2a9 9 0 1 0 0 18 9 9 0 0 0 0-18zm.348 7v4m0 4h.01"
              />
            </svg>
            <div className="text-yellow-700 text-sm">
              {warnings.map(({ text }, index) => (
                <p key={`date-warning-${index}`}>{text}</p>
              ))}
            </div>
          </div>
        )}
      </>
    );
  }
}

export default withTranslation()(MobileDatesStep);
